import { DatePicker, Dropdown, NumberField, Section, TextField, AutoComplete } from 'm2m.components';
import React, { useEffect, useState, useCallback } from 'react';
import { Typography, TextField as MUITextField, Chip } from '@material-ui/core';
import { connect, useDispatch, useSelector } from 'react-redux';
import 'assets/style.css';
import { BooleanValue } from 'common/genericOptions';
import {
  FuncaoParticipante,
  ParticipanteResponse,
  BancoResponse,
  GrupoAcesso,
  InstrumentoFinanceiro,
  TipoRegistroInstrumentoFinanceiro,
} from 'common/genericTypes';
import { RootState } from 'store/index';
import { updateField } from 'store/modules/certificadoRecebiveis/caracteristicas';
import { updateId } from 'store/modules/certificadoRecebiveis/emissor';
import { addCpfCnpjMask } from 'utils/mask';
import BancoAutoComplete from 'components/operacoes/bancoAutoComplete';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import { labelsCaracteristicas as labels } from '../labels';
import { useValidarCampos, EtapaCadastro } from '../useValidarCampos';
import { CpfCnpjMask } from 'components/masks';
import { addAlert } from 'store/modules/snackBar';
import DistribuicaoPublicaSection from 'components/instrumentos/registro/caracteristicas/distribuicaoPublicaSection';
import { useFieldDisabledConfig } from 'common/fieldDisabledConfig';

const mapStateToProps = (state: RootState) => ({
  emissorId: state.certificadoRecebiveisCadastro.emissor.id,
  caracteristicas: state.certificadoRecebiveisCadastro.caracteristicas,
  highlightErrors: state.certificadoRecebiveisCadastro.highlightErrors,
  metadata: state.certificadoRecebiveisCadastro.metadata,
  participanteApi: state.participanteApi,
  usuarioApi: state.usuarioApi,
  isUsuarioOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes,
  isUsuarioCadastroOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes,
  isUsuarioCredenciado: state.auth.usuario.participante.funcao === FuncaoParticipante.Credenciado,
  isUsuarioCustodiante: state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante,
  isUsuarioEmissor: state.auth.usuario.participante.funcao === FuncaoParticipante.Emissor,
  isUsuarioAgenteFiduciario: state.auth.usuario.participante.funcao === FuncaoParticipante.AgenteFiduciario,
  isUsuarioEscriturador: state.auth.usuario.participante.funcao === FuncaoParticipante.Escriturador,
  participanteCnpj: state.auth.usuario.participante.cnpj,
  participanteId: state.auth.usuario.participante.id,
  typeOfRegister: state.certificadoRecebiveisCadastro.typeOfRegister,
});

const mapDispatchToProps = { updateField, updateId };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & { participantes: ParticipanteResponse[], bancos: BancoResponse[], codigo?: string, delimitacaoEfetuada?: boolean };

const Caracteristicas: React.FC<Props> = (props) => {
  const {
    emissorId,
    caracteristicas,
    caracteristicas: {
      cpfCnpjAgenteFiduciario,
      cpfCnpjGarantidor,
      cnpjEscriturador,
      dataEncerramentoDistribuicao,
      coordenadorLiderId,
      coordenadoresIds,
      regimeFiduciario,
      distribuicaoPublica,
      tipoLiquidacao,
      nomeResponsavelPelasInformacoesDRI,
      emailResponsavelPelasInformacoesDRI,
    },
    highlightErrors,
    participanteApi: { getParticipantesSolicitadosPeloUsuario },
    usuarioApi: { getUsuarioMaster },
    updateField,
    updateId,
    metadata: {
      options,
      naturezaPessoa,
      tipoDistribuicaoPublica,
      series,
      negociacoesSecundarias,
      depositariaEmissoes,
      unidadesFederativas,
      tiposLiquidacao,
      contraparteRecebedora,
    },
    isUsuarioEmissor,
    isUsuarioCustodiante,
    isUsuarioAgenteFiduciario,
    isUsuarioEscriturador,
    participanteCnpj,
    participanteId,
    participantes,
    bancos,
    typeOfRegister,
    codigo,
    delimitacaoEfetuada = false,
  } = props;

  const [showRegimeFiduciario, setShowRegimeFiduciario] = useState<boolean>(
    regimeFiduciario.value === BooleanValue.Yes
  );
  const [showDistribuicaoPublica, setShowDistribuicaoPublica] = useState<boolean>(
    distribuicaoPublica.value === BooleanValue.Yes
  );

  const [bancoLiquidanteEmissor, setBancoLiquidanteEmissor] = useState<BancoResponse | null>(null);
  const [coordenadoresOptions, setCoordenadoresOptions] = useState<Array<ParticipanteResponse>>([]);
  const [escriturador, setEscriturador] = useState<ParticipanteResponse | null>(null);
  const fieldDisabledConfig = useFieldDisabledConfig(typeOfRegister);
  const [escrituradores, setEscrituradores] = useState<Array<ParticipanteResponse>>([]);
  const [emissores, setEmissores] = useState<ParticipanteResponse[]>([]);
  const [emissor, setEmissor] = useState<ParticipanteResponse | null>(null);
  const getNumeroEmissoesApi = useSelector((state: RootState) => state.instrumentoApi.getNumeroEmissoes);

  const dispatch = useDispatch();
  const camposInvalidos = useValidarCampos(EtapaCadastro.Caracteristicas);

  const showError = (label: string) => camposInvalidos.includes(label) && highlightErrors;

  const onlyDigits = (v?: string) => (v ? v.replace(/\D/g, '') : '');
  const isAditamento = typeOfRegister === TipoRegistroInstrumentoFinanceiro.Aditamento;
  const disableAfFields = isAditamento && isUsuarioAgenteFiduciario && onlyDigits(cpfCnpjAgenteFiduciario) === onlyDigits(participanteCnpj);
  const disableEscriturador = isAditamento && isUsuarioEscriturador && onlyDigits(cnpjEscriturador) === onlyDigits(participanteCnpj);

  const setParticipantes = useCallback(async () => {
    try {
      setEscrituradores(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.Escriturador)));
      setCoordenadoresOptions(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.Coordenador)));
      setEmissores(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.Emissor)));
    } catch { }
  }, [participantes]);

  const getUsuarioMasterByEmissorId = useCallback(
    async (id: string) => {
      try {
        const result = await getUsuarioMaster(dispatch, id, true);
        if (result && result.nome && result.email) {
          updateField({ name: 'nomeResponsavelPelasInformacoesDRI', value: result.nome });
          updateField({ name: 'emailResponsavelPelasInformacoesDRI', value: result.email });
          dispatch(addAlert({ message: 'Campos de email e nome do responsável atualizados.', severity: 'info' }));
        }
      } catch { }
    },
    [getUsuarioMaster]
  );

  const getNumeroEmissoes = useCallback(async (
    cnpj: string
  ) => {
    try {
      const result = await getNumeroEmissoesApi(
        dispatch,
        cnpj,
        InstrumentoFinanceiro.CertificadoRecebiveis
      );
      const numero = parseInt(result) + 1;
      updateField({ name: 'numeroEmissao', value: numero });
      updateField({ name: 'cnpjEmissor', value: cnpj });
    }
    catch { }
  }, [dispatch, getNumeroEmissoesApi]);


  const handleEmissorChange = (value: ParticipanteResponse | null) => {
    setEmissor(value);
    updateId(value ? value.id : '');
    if (value?.cnpj) getNumeroEmissoes(value.cnpj);

    if (value) {
      const bancoEmissor = bancos.find(
        (b) => value.ispbBancoLiquidante === b.ispb || value.ispbBancoLiquidante === b.cnpj
      );
      if (bancoEmissor) handleBancoLiquidanteChange(bancoEmissor);

      if (!(nomeResponsavelPelasInformacoesDRI && emailResponsavelPelasInformacoesDRI)) {
        if (isUsuarioEmissor) getUsuarioMasterByEmissorId(participanteId);
        else getUsuarioMasterByEmissorId(value.id);
      }
    }
  };

  const handleEscrituradorChange = (value: ParticipanteResponse | null) => {
    setEscriturador(value);
    updateField({ name: 'razaoSocialEscriturador', value: value?.razaoSocial ?? '' });
    updateField({ name: 'cnpjEscriturador', value: value?.cnpj ?? '' });
  };

  const handleBancoLiquidanteChange = (value: BancoResponse | null) => {
    setBancoLiquidanteEmissor(value);
    updateField({ name: 'bancoLiquidanteEmissor', value: value?.fullname ?? '' });
    updateField({ name: 'cnpjBancoLiquidanteEmissor', value: value?.cnpj ?? '' });
    updateField({ name: 'codigoBancoContaCorrenteVinculadaEmissao', value: value?.code ?? '' });
  };

  useEffect(() => {
    const newStatus = regimeFiduciario.value === BooleanValue.Yes;
    if (showRegimeFiduciario !== newStatus) {
      updateField({ name: 'coobrigacao', value: options[0] });
      updateField({ name: 'naturezaAgenteFiduciario', value: naturezaPessoa[0] });
      updateField({ name: 'razaoSocialAgenteFiduciario', value: '' });
      updateField({ name: 'cpfCnpjAgenteFiduciario', value: '' });
    }
    setShowRegimeFiduciario(newStatus);
  }, [regimeFiduciario, updateField, showRegimeFiduciario, options, naturezaPessoa]);

  useEffect(() => {
    const newStatus = distribuicaoPublica.value === BooleanValue.Yes;
    if (showDistribuicaoPublica !== newStatus) {
      updateField({ name: 'tipoDistribuicaoPublica', value: tipoDistribuicaoPublica[0] });
      updateField({ name: 'coordenadorLiderId', value: '' });
      updateField({ name: 'coordenadoresIds', value: [] });
      updateField({ name: 'dataEncerramentoDistribuicao', value: '' });
    }
    setShowDistribuicaoPublica(newStatus);
  }, [distribuicaoPublica, showDistribuicaoPublica, updateField, tipoDistribuicaoPublica]);

  useEffect(() => {
    if (emissor && !caracteristicas.id) {
      getNumeroEmissoes(emissor.cnpj);
    }
  }, [emissor, emissorId]);

  useEffect(() => {
    if (cnpjEscriturador) setEscriturador(escrituradores.find((c) => c.cnpj === cnpjEscriturador) ?? null);
  }, [escrituradores, cnpjEscriturador]);

  useEffect(() => {
    if (emissores.length > 0) setEmissor(emissores.find((c) => c.id === emissorId) ?? null);
  }, [emissores, emissorId]);

  useEffect(() => {
    if (caracteristicas.bancoLiquidanteEmissor) {
      const banco = bancos.find((b) => b.fullname === caracteristicas.bancoLiquidanteEmissor);
      if (banco) setBancoLiquidanteEmissor(banco);
    }
  }, [bancos, caracteristicas.bancoLiquidanteEmissor]);

  useEffect(() => {
    setParticipantes();
  }, [setParticipantes]);

  return (
    <div>
      <Section title="Emissor" defaultGrid>
        <ParticipanteAutoComplete
          label={`${labels.emissor} *`}
          participantes={emissores}
          participante={emissor}
          onChange={handleEmissorChange}
          error={showError(labels.emissor)}
          disabled={fieldDisabledConfig.emissor || [
            TipoRegistroInstrumentoFinanceiro.Aditamento,
            TipoRegistroInstrumentoFinanceiro.EdicaoEmitido
          ].includes(typeOfRegister)}
        />
      </Section>
      <Section title="Características da Emissão" defaultGrid>
        <TextField
          label={labels.numeroEmissao}
          value={caracteristicas.numeroEmissao}
          onChange={(value) => {
            const numericValue = value.replace(/\D/g, '');
            updateField({ name: 'numeroEmissao', value: numericValue });
          }}
          disabled={fieldDisabledConfig.numeroEmissao}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          required
          error={showError(labels.numeroEmissao)}
        />
        <TextField
          label={labels.numeroSerie}
          value={caracteristicas.numeroSerie}
          onChange={(value) => updateField({ name: 'numeroSerie', value })}
          disabled={fieldDisabledConfig.numeroSerie}
          required
          error={showError(labels.numeroSerie)}
        />
        <Dropdown
          label={labels.tipoSerie}
          options={series}
          value={caracteristicas.tipoSerie.value}
          onChange={(value) =>
            updateField({
              name: 'tipoSerie',
              value: series.find((s) => s.value === value) ?? series[0],
            })
          }
          disabled={fieldDisabledConfig.tipoSerie}
        />
        <Dropdown
          label={labels.possuiVasosComunicantes}
          options={options}
          value={caracteristicas.possuiVasosComunicantes.value}
          onChange={(value) =>
            updateField({
              name: 'possuiVasosComunicantes',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
        />
        <Dropdown
          label={labels.regimeFiduciario}
          required
          options={options}
          value={regimeFiduciario.value}
          onChange={(value) =>
            updateField({
              name: 'regimeFiduciario',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
          error={showError(labels.regimeFiduciario)}
        />
        <Dropdown
          label={labels.distribuicaoPublica}
          required
          options={options}
          value={distribuicaoPublica.value}
          onChange={(value) => {
            const distribuicaoPublica = options.find((s) => s.value === value) ?? options[0];
            updateField({
              name: 'distribuicaoPublica',
              value: distribuicaoPublica,
            });
          }}
          error={showError(labels.distribuicaoPublica)}
        />
        <Dropdown
          label={labels.negociacaoSecundariaPor}
          required
          options={negociacoesSecundarias}
          value={caracteristicas.negociacaoSecundariaPor.value}
          onChange={(value) =>
            updateField({
              name: 'negociacaoSecundariaPor',
              value: negociacoesSecundarias.find((s) => s.value === value) ?? negociacoesSecundarias[0],
            })
          }
          error={showError(labels.negociacaoSecundariaPor)}
        />
        <Dropdown
          label={labels.termoSecuritizacaoFormalizado}
          required
          options={options}
          value={caracteristicas.termoSecuritizacaoFormalizado.value}
          onChange={(value) =>
            updateField({
              name: 'termoSecuritizacaoFormalizado',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
          error={showError(labels.termoSecuritizacaoFormalizado)}
        />
        <Dropdown
          label={labels.depositariaEmissao}
          required
          options={depositariaEmissoes}
          value={caracteristicas.depositariaEmissao.value}
          onChange={(value) =>
            updateField({
              name: 'depositariaEmissao',
              value: depositariaEmissoes.find((s) => s.value === value) ?? depositariaEmissoes[0],
            })
          }
          error={showError(labels.depositariaEmissao)}
        />
        <TextField
          label={labels.codigoCvmCiaSecuritizadora}
          required
          value={caracteristicas.codigoCvmCiaSecuritizadora}
          onChange={(value) => updateField({ name: 'codigoCvmCiaSecuritizadora', value })}
          error={showError(labels.codigoCvmCiaSecuritizadora)}
        />
        <Dropdown
          label={labels.utilizacaoAnuncioInicioDistribuicao}
          options={options}
          value={caracteristicas.utilizacaoAnuncioInicioDistribuicao.value}
          onChange={(value) =>
            updateField({
              name: 'utilizacaoAnuncioInicioDistribuicao',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
        />
        <Dropdown
          label={labels.ufLocalEmissao}
          required
          options={unidadesFederativas}
          value={caracteristicas.ufLocalEmissao.value}
          onChange={(value) =>
            updateField({
              name: 'ufLocalEmissao',
              value: unidadesFederativas.find((s) => s.value === value) ?? unidadesFederativas[0],
            })
          }
          disabled={fieldDisabledConfig.ufLocalEmissao}
          error={showError(labels.ufLocalEmissao)}
        />
        <TextField
          label={labels.localEmissao}
          required
          value={caracteristicas.localEmissao}
          onChange={(value) => updateField({ name: 'localEmissao', value })}
          disabled={fieldDisabledConfig.localEmissao}
          error={showError(labels.localEmissao)}
        />
        <TextField
          label={labels.classificadoraRisco1}
          value={caracteristicas.classificadoraRisco1}
          onChange={(value) => updateField({ name: 'classificadoraRisco1', value })}
        />
        <TextField
          label={labels.rating1}
          required={!!caracteristicas.classificadoraRisco1}
          value={caracteristicas.rating1}
          onChange={(value) => updateField({ name: 'rating1', value })}
          error={showError(labels.classificadoraRisco1)}
        />
        <TextField
          label={labels.classificadoraRisco2}
          value={caracteristicas.classificadoraRisco2}
          onChange={(value) => updateField({ name: 'classificadoraRisco2', value })}
        />
        <TextField
          label={labels.rating2}
          required={!!caracteristicas.classificadoraRisco2}
          value={caracteristicas.rating2}
          onChange={(value) => updateField({ name: 'rating2', value })}
          error={showError(labels.classificadoraRisco2)}
        />
        <Dropdown
          label={labels.ufLocalPagamento}
          required
          options={unidadesFederativas}
          value={caracteristicas.ufLocalPagamento.value}
          onChange={(value) =>
            updateField({
              name: 'ufLocalPagamento',
              value: unidadesFederativas.find((s) => s.value === value) ?? unidadesFederativas[0],
            })
          }
          disabled={fieldDisabledConfig.ufLocalPagamento}
          error={showError(labels.ufLocalPagamento)}
        />
        <TextField
          label={labels.localPagamento}
          required
          value={caracteristicas.localPagamento}
          onChange={(value) => updateField({ name: 'localPagamento', value })}
          disabled={fieldDisabledConfig.localPagamento}
          error={showError(labels.localPagamento)}
        />
        <TextField
          label={labels.nomeResponsavelPelasInformacoesDRI}
          required
          value={caracteristicas.nomeResponsavelPelasInformacoesDRI}
          onChange={(value) => updateField({ name: 'nomeResponsavelPelasInformacoesDRI', value })}
          error={showError(labels.nomeResponsavelPelasInformacoesDRI)}
        />
        <TextField
          label={labels.emailResponsavelPelasInformacoesDRI}
          required
          value={caracteristicas.emailResponsavelPelasInformacoesDRI}
          onChange={(value) => updateField({ name: 'emailResponsavelPelasInformacoesDRI', value })}
          error={showError(labels.emailResponsavelPelasInformacoesDRI)}
        />
        <Dropdown
          label={labels.tipoLiquidacao}
          required
          options={tiposLiquidacao}
          value={tipoLiquidacao.value}
          onChange={(value) => {
            const liquidacaoOption = tiposLiquidacao.find((s) => s.value === value) ?? tiposLiquidacao[0];
            updateField({
              name: 'tipoLiquidacao',
              value: liquidacaoOption,
            });
          }}
          disabled={fieldDisabledConfig.tipoLiquidacao}
          error={showError(labels.tipoLiquidacao)}
        />
        <BancoAutoComplete
          label={`${labels.bancoLiquidanteEmissor} *`}
          bancos={bancos}
          banco={bancoLiquidanteEmissor}
          onChange={(value: BancoResponse | null) => {
            setBancoLiquidanteEmissor(value);
            updateField({ name: 'bancoLiquidanteEmissor', value: value?.fullname ?? '' });
            updateField({ name: 'cnpjBancoLiquidanteEmissor', value: value?.cnpj ?? '' });
            updateField({ name: 'codigoBancoContaCorrenteVinculadaEmissao', value: value?.code ?? '' });
          }}
          disabled={fieldDisabledConfig.bancoLiquidanteEmissor}
          error={showError(labels.bancoLiquidanteEmissor)}
        />
        <TextField
          label={labels.numeroAgenciaContaCorrenteVinculadaEmissao}
          required
          value={caracteristicas.numeroAgenciaContaCorrenteVinculadaEmissao}
          onChange={(value) => updateField({ name: 'numeroAgenciaContaCorrenteVinculadaEmissao', value })}
          disabled={fieldDisabledConfig.numeroAgenciaContaCorrenteVinculadaEmissao}
          error={showError(labels.numeroAgenciaContaCorrenteVinculadaEmissao)}
        />
        <TextField
          label={labels.numeroContaCorrenteVinculadaEmissao}
          required
          value={caracteristicas.numeroContaCorrenteVinculadaEmissao}
          onChange={(value) => updateField({ name: 'numeroContaCorrenteVinculadaEmissao', value })}
          disabled={fieldDisabledConfig.numeroContaCorrenteVinculadaEmissao}
          error={showError(labels.numeroContaCorrenteVinculadaEmissao)}
        />
        <TextField
          label={labels.chaveExterna}
          value={caracteristicas.chaveExterna}
          onChange={(value) => updateField({ name: 'chaveExterna', value })}
          disabled={fieldDisabledConfig.chaveExterna}
        />
        {!showDistribuicaoPublica && (
          <ParticipanteAutoComplete
            label={`${labels.escriturador} *`}
            participantes={escrituradores}
            participante={escriturador}
            onChange={handleEscrituradorChange}
            error={showError(labels.escriturador)}
            disabled={fieldDisabledConfig.escriturador}
          />
        )}
        <TextField
          label={labels.isin}
          value={caracteristicas.isin}
          onChange={(value) => updateField({ name: 'isin', value })}
        />
        <TextField
          label={labels.descricaoAdicional}
          value={caracteristicas.descricaoAdicional}
          multiline
          rows={4}
          onChange={(value) => updateField({ name: 'descricaoAdicional', value })}
          disabled={fieldDisabledConfig.descricaoAdicional}
        />
      </Section>
      <Section title="Informações do Lastro" defaultGrid>
        <Dropdown
          label={labels.averbadoEmCartorio}
          required
          options={options}
          value={caracteristicas.averbadoEmCartorio.value}
          onChange={(value) =>
            updateField({
              name: 'averbadoEmCartorio',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
          error={showError(labels.averbadoEmCartorio)}
        />
        <Dropdown
          label={labels.registroNaInstituicaoCustodiante}
          required
          options={options}
          value={caracteristicas.registroNaInstituicaoCustodiante.value}
          onChange={(value) =>
            updateField({
              name: 'registroNaInstituicaoCustodiante',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
          error={showError(labels.registroNaInstituicaoCustodiante)}
        />
        <Dropdown
          label={labels.garantiaFlutuante}
          required
          options={options}
          value={caracteristicas.garantiaFlutuante.value}
          onChange={(value) =>
            updateField({
              name: 'garantiaFlutuante',
              value: options.find((s) => s.value === value) ?? options[0],
            })
          }
          error={showError(labels.garantiaFlutuante)}
        />
        <TextField
          label={labels.tipoGarantia}
          required
          value={caracteristicas.tipoGarantia}
          onChange={(value) => updateField({ name: 'tipoGarantia', value })}
          error={showError(labels.tipoGarantia)}
        />
        <TextField
          label={labels.ativoGarantia}
          required
          value={caracteristicas.ativoGarantia}
          onChange={(value) => updateField({ name: 'ativoGarantia', value })}
          error={showError(labels.ativoGarantia)}
        />
        <TextField
          label={labels.nomeGarantidor}
          required
          value={caracteristicas.nomeGarantidor}
          onChange={(value) => updateField({ name: 'nomeGarantidor', value })}
          error={showError(labels.nomeGarantidor)}
        />
        <TextField
          label={`${labels.cpfCnpjGarantidor} *`}
          value={CpfCnpjMask(cpfCnpjGarantidor)}
          onChange={(value) => {
            updateField({ name: 'cpfCnpjGarantidor', value });
          }}
          error={showError(labels.cpfCnpjGarantidor)}
          errorMessage={'CNPJ/CPF Inválido'}
        />
        <Dropdown
          label={labels.naturezaGarantidor}
          required
          options={naturezaPessoa}
          value={caracteristicas.naturezaGarantidor.value}
          onChange={(value) =>
            updateField({
              name: 'naturezaGarantidor',
              value: naturezaPessoa.find((s) => s.value === value) ?? naturezaPessoa[0],
            })
          }
          error={showError(labels.naturezaGarantidor)}
        />
        <NumberField
          label={labels.valorGarantia}
          required
          value={caracteristicas.valorGarantia}
          onChange={(value) => updateField({ name: 'valorGarantia', value })}
          numberType="money"
          error={showError(labels.valorGarantia)}
        />
      </Section>
      {showRegimeFiduciario && (
        <Section title="Regime Fiduciário" defaultGrid>
          <Dropdown
            label={labels.coobrigacao}
            required
            options={options}
            value={caracteristicas.coobrigacao.value}
            onChange={(value) =>
              updateField({
                name: 'coobrigacao',
                value: options.find((s) => s.value === value) ?? options[0],
              })
            }
            error={showError(labels.coobrigacao)}
          />
          <Dropdown
            label={labels.naturezaAgenteFiduciario}
            options={naturezaPessoa}
            value={caracteristicas.naturezaAgenteFiduciario.value}
            onChange={(value) =>
              updateField({
                name: 'naturezaAgenteFiduciario',
                value: naturezaPessoa.find((s) => s.value === value) ?? naturezaPessoa[0],
              })
            }
            disabled={disableAfFields}
          />
          <TextField
            label={labels.razaoSocialAgenteFiduciario}
            required
            value={caracteristicas.razaoSocialAgenteFiduciario}
            onChange={(value) => updateField({ name: 'razaoSocialAgenteFiduciario', value })}
            error={showError(labels.razaoSocialAgenteFiduciario)}
            disabled={disableAfFields}
          />
          <TextField
            label={`${labels.cpfCnpjAgenteFiduciario} *`}
            value={CpfCnpjMask(cpfCnpjAgenteFiduciario ?? '')}
            onChange={(value) => {
              updateField({ name: 'cpfCnpjAgenteFiduciario', value });
            }}
            error={showError(labels.cpfCnpjAgenteFiduciario)}
            errorMessage={'CNPJ/CPF Inválido'}
            disabled={disableAfFields}
          />
        </Section>
      )}
      <DistribuicaoPublicaSection
        showDistribuicaoPublica={showDistribuicaoPublica}
        labels={labels}
        tipoDistribuicaoPublica={tipoDistribuicaoPublica}
        caracteristicas={caracteristicas}
        updateField={updateField}
        dataEncerramentoDistribuicao={dataEncerramentoDistribuicao}
        coordenadoresOptions={coordenadoresOptions}
        coordenadoresIds={coordenadoresIds}
        coordenadorLiderId={coordenadorLiderId}
        escrituradores={escrituradores}
        escriturador={escriturador}
        contraparteRecebedora={contraparteRecebedora}
        handleEscrituradorChange={handleEscrituradorChange}
        showError={showError}
        codigo={codigo}
        delimitacaoEfetuada={delimitacaoEfetuada}
        escrituradorDisabled={disableEscriturador}
      />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Caracteristicas);
